import { DoctorVerificationList, VerificationStats } from 'data';
import { API, axiosInstance, handleResponse } from './helper';
import { getRealmUserInfo } from './keycloak';
import { Doctor } from '../pages/DoctorsVerification/types';
import { getPractiseType } from '../utils';

interface updateVerificationStatusPayload {
  status: string;
  practiseAdminId: string;
}

interface updateOverAllStatusPayload {
  status: string;
  practiseAdminId: string;
}

interface updateStatusPayloadAfterRole {
  practiseUserId: string;
  roleAssigned: boolean;
}
interface DoctorVerificationStatus {
  status: string;
  practiseAdminId: string; // UUID for the practice admin
  feedback: string; // Feedback or comments from the reviewer
  reviewerId: string; // UUID for the reviewer
  section: string; // Section or context of the status (e.g., "Reject Profile")
}



const getDoctorVerificationList = (role: string, verificationStatus: string, limit?: number,
  updatedAt?: string) => {
  const api = `${API.getDoctorVerificationList}?verificationStatus=${verificationStatus}&practiseType=${getPractiseType(role)}&limit=${limit || 10
    }&updatedAt=${updatedAt || new Date().toISOString()}`;
  return handleResponse(axiosInstance.get(api));

};

const getDoctorVerificationDetails = (practiseAdminId: string) => {
  return handleResponse(
    axiosInstance.get(`${API.getDoctorVerificationDetails}?practiseAdminId=${practiseAdminId}`)
  );
};

const getDoctorVerificationStats = async (role: string): Promise<VerificationStats> => {
  const response = await axiosInstance.get(
    `${API.getDoctorVerificationStats}?practiseType=${getPractiseType(role)}`
  );
  return response.data;
};

const updateProfessionalDetailsVerificationStatus = (
  update_verification_status_payload: updateVerificationStatusPayload
) =>
  handleResponse(
    axiosInstance.put(
      API.updateProfessionalDetailsVerificationStatus,
      update_verification_status_payload
    )
  );

const updateIdDocumentsVerificationStatus = (
  update_id_status_payload: updateVerificationStatusPayload
) =>
  handleResponse(
    axiosInstance.put(API.updateIdDocumentsVerificationStatus, update_id_status_payload)
  );

const updateLivePictureVerificationStatus = (
  update_live_pic_status_payload: updateVerificationStatusPayload
) =>
  handleResponse(
    axiosInstance.put(API.updateLivePictureVerificationStatus, update_live_pic_status_payload)
  );

const updateOrgVerificationStatus = (update_org_status_payload: updateVerificationStatusPayload) =>
  handleResponse(axiosInstance.put(API.updateOrgVerificationStatus, update_org_status_payload));

const askForUpdateProfessionalDetails = (
  ask_for_update_verification_status_payload: updateVerificationStatusPayload
) =>
  handleResponse(
    axiosInstance.post(
      API.askForUpdateProfessionalDetails,
      ask_for_update_verification_status_payload
    )
  );

const askForUpdateMedicalDocument = (update_id_status_payload: updateVerificationStatusPayload) =>
  handleResponse(axiosInstance.post(API.askForUpdateMedicalDocument, update_id_status_payload));

const askForUpdateLivePicture = (update_live_pic_status_payload: updateVerificationStatusPayload) =>
  handleResponse(axiosInstance.post(API.askForUpdateLivePicture, update_live_pic_status_payload));

const askForUpdateOrganisationDetails = (
  update_org_status_payload: updateVerificationStatusPayload
) =>
  handleResponse(
    axiosInstance.post(API.askForUpdateOrganisationDetails, update_org_status_payload)
  );

const updatePractiseOverAllVerificationStatus = (
  update_over_all_status_payload: updateOverAllStatusPayload
) =>
  handleResponse(
    axiosInstance.put(API.updatePractiseOverAllVerificationStatus, update_over_all_status_payload)
  );

const setStatusAfterRole = (status_payload: updateStatusPayloadAfterRole) =>
  handleResponse(axiosInstance.put(API.setStatusAfterRole, status_payload));

const rejectProfile = (reject_payload: DoctorVerificationStatus) =>
  handleResponse(axiosInstance.post(API.rejectProfile, reject_payload));

// Utility function to format doctors list
async function getFormattedDoctorsList(
  selectedStat: string | undefined,
  list: DoctorVerificationList,
  user_access_token: string
): Promise<Doctor[]> {
  if (!list?.practiseBasicDetails?.length) return [];
  // Filter the list based on the selected status
  const filteredList = selectedStat
    ? list.practiseBasicDetails.filter((item) => item?.verificationStatus === selectedStat)
    : list.practiseBasicDetails;

  // Fetch doctor info for each filtered entry
  const doctorPromises = filteredList.map(async (doc) => {
    try {
      const { id, email, firstName, lastName } = await getRealmUserInfo(
        doc.practiseAdminId,
        user_access_token
      );
      return { ...doc, id, email, firstName, lastName,userPractiseMappingRelation:doc.userPractiseMappingRelation } as Doctor;
    } catch (error) {
      console.warn(`Failed to fetch data for admin ID ${doc.practiseAdminId}:`, error);
      return null;
    }
  });
  // Resolve all promises and filter out null results
  const resolvedDoctors = await Promise.all(doctorPromises);
  return resolvedDoctors.filter((doc): doc is Doctor => doc !== null);
}

export {
  getDoctorVerificationList,
  getDoctorVerificationDetails,
  getDoctorVerificationStats,
  updateProfessionalDetailsVerificationStatus,
  updateIdDocumentsVerificationStatus,
  updateLivePictureVerificationStatus,
  updateOrgVerificationStatus,
  updatePractiseOverAllVerificationStatus,
  setStatusAfterRole,
  rejectProfile,
  askForUpdateProfessionalDetails,
  askForUpdateMedicalDocument,
  askForUpdateLivePicture,
  askForUpdateOrganisationDetails,
  getFormattedDoctorsList
};
